import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { createListCollection } from "@ark-ui/react";
import { useField, useForm } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import { StaticMapOverlay, api, StaticOverlayMapLayer } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import Select from "@/components/select/select";
import { required } from "@/features/shared/components/mui-rff-fields/validation-helpers";

type StaticConfigurationProps = { name: string; initialValue: StaticOverlayMapLayer };

const COLOR_OPTIONS = ["white", "grey", "black"] as const;

const StaticConfiguration = ({ name, initialValue }: StaticConfigurationProps) => {
  const { t } = useTranslation();
  const staticQuery = useQuery({
    queryKey: ["staticLayerMapOverlays"],
    queryFn: (): Promise<StaticMapOverlay[]> =>
      unwrapAxiosData(api.staticMapOverlays.getStaticMapOverlays()),
  });

  const form = useForm();
  const staticMapOverlayOptions = useMemo(() => {
    if (!staticQuery.isSuccess || !staticQuery.data) return [];

    return staticQuery.data.map((option) => ({
      value: option.id,
      label: option.name,
    }));
  }, [staticQuery]);
  const overlayCollection = createListCollection({
    items: staticMapOverlayOptions,
  });
  const overlayField = useField(`${name}.static_overlay_id`, {
    defaultValue: initialValue.static_overlay_id,
  });

  const colorOptions = useMemo(() => {
    return Object.values(COLOR_OPTIONS).map((option) => ({
      value: option,
      label: t(`colors.${option}`),
    }));
  }, []);
  const colorCollection = createListCollection({
    items: colorOptions,
  });
  const colorField = useField(`${name}.color`, {
    validate: required,
    defaultValue: "white",
  });

  return (
    <>
      <Select
        label={t("admin.widgets.fields.map.static_overlay.overlay")}
        placeholder="Select an overlay"
        disabled={!staticQuery.isSuccess}
        collection={overlayCollection}
        name={overlayField.input.name}
        value={overlayField.input.value}
        onValueChange={(value) => form.change(overlayField.input.name, value)}
        error={overlayField.meta.touched && overlayField.meta.error}
      />

      <Select
        label={t("admin.widgets.fields.map.static_overlay.color")}
        collection={colorCollection}
        name={colorField.input.name}
        value={colorField.input.value}
        onValueChange={(value) => form.change(colorField.input.name, value)}
        error={colorField.meta.touched && colorField.meta.error}
      />
    </>
  );
};

export default StaticConfiguration;
